/**
 * SGMembers Dashboard Widget
 *
 * Embedded loyalty card for Shopify storefront pages.
 * No build step required — pure vanilla JS, no dependencies.
 *
 * Flow:
 *  1. Page loads → widget container already in DOM (from Liquid section)
 *  2. Widget shows skeleton immediately (zero wait)
 *  3. Single fetch to /api/members/storefront-data returns all data
 *  4. Widget renders: points balance, tier, progress, vouchers, CTA
 *
 * Requires: sgmembers-dashboard.css
 */

(function () {
  'use strict';

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'className') { node.className = attrs[k]; }
        else if (k === 'style') { node.setAttribute('style', attrs[k]); }
        else if (k === 'html')  { node.innerHTML = attrs[k]; }
        else                    { node.setAttribute(k, attrs[k]); }
      });
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach(function (c) {
        if (c) node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      });
    }
    return node;
  }

  function fmtPoints(n) {
    return Number(n || 0).toLocaleString();
  }

  function fmtCurrency(n) {
    return '$' + Number(n || 0).toFixed(2);
  }

  function tierProgress(member) {
    // Determine next tier threshold from tier.maxSpend or a fixed ladder
    var spend    = member.lifetimeSpend || 0;
    var maxSpend = member.tier && member.tier.maxSpend != null ? member.tier.maxSpend : null;
    var minSpend = member.tier ? (member.tier.minSpend || 0) : 0;
    if (maxSpend === null) return { pct: 100, label: 'Highest tier — no limit' };
    var range = maxSpend - minSpend;
    if (range <= 0) return { pct: 100, label: '' };
    var pct   = Math.min(100, Math.round(((spend - minSpend) / range) * 100));
    var rem   = Math.max(0, maxSpend - spend);
    return {
      pct: pct,
      label: fmtCurrency(rem) + ' to next tier',
    };
  }

  // ─── Skeleton ─────────────────────────────────────────────────────────────

  function renderSkeleton(container) {
    container.innerHTML = '';
    var wrap = el('div', { className: 'sgm-dash__skeleton' }, [
      el('div', { className: 'sgm-skel-line', style: 'height:14px;width:40%;margin-bottom:20px;' }),
      el('div', { className: 'sgm-skel-line', style: 'height:48px;width:55%;margin-bottom:10px;' }),
      el('div', { className: 'sgm-skel-line', style: 'height:4px;width:100%;margin-bottom:20px;' }),
      el('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:1px;margin-bottom:16px;' }, [
        el('div', { className: 'sgm-skel-line', style: 'height:64px;' }),
        el('div', { className: 'sgm-skel-line', style: 'height:64px;' }),
      ]),
      el('div', { className: 'sgm-skel-line', style: 'height:44px;width:160px;border-radius:4px;' }),
    ]);
    container.appendChild(wrap);
  }

  // ─── Error ────────────────────────────────────────────────────────────────

  function renderError(container, msg) {
    container.innerHTML = '';
    container.appendChild(
      el('div', { className: 'sgm-dash__error' }, [
        el('p', { style: 'margin-bottom:12px;' }, msg || 'Could not load your rewards.'),
        el('button', {
          className: 'sgm-dash__btn sgm-dash__btn--primary',
          style: 'cursor:pointer;border:none;',
        }, 'Retry'),
      ])
    );
    container.querySelector('button').addEventListener('click', function () {
      window.location.reload();
    });
  }

  // ─── Voucher row ──────────────────────────────────────────────────────────

  function renderVoucher(coupon) {
    var copyBtn = el('button', { className: 'sgm-dash__voucher-copy' }, 'Copy');

    copyBtn.addEventListener('click', function () {
      var btn = this;
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(coupon.shopifyDiscountCode).then(function () {
          btn.textContent = 'Copied!';
          btn.classList.add('copied');
          setTimeout(function () {
            btn.textContent = 'Copy';
            btn.classList.remove('copied');
          }, 2000);
        });
      } else {
        // Fallback for non-secure contexts (e.g. inside Shopify preview)
        var ta = document.createElement('textarea');
        ta.value = coupon.shopifyDiscountCode;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        btn.textContent = 'Copied!';
        btn.classList.add('copied');
        setTimeout(function () {
          btn.textContent = 'Copy';
          btn.classList.remove('copied');
        }, 2000);
      }
    });

    var valueText = coupon.discountType === 'percentage'
      ? coupon.discountValue + '% off'
      : fmtCurrency(coupon.discountValue) + ' off';

    return el('div', { className: 'sgm-dash__voucher' }, [
      el('div', {}, [
        el('div', { className: 'sgm-dash__voucher-code' }, coupon.shopifyDiscountCode),
        el('div', { className: 'sgm-dash__voucher-value' }, valueText),
      ]),
      copyBtn,
    ]);
  }

  // ─── Main render ──────────────────────────────────────────────────────────

  function renderDashboard(container, data, opts) {
    var member    = data.member;
    var coupons   = data.coupons   || [];
    var rewards   = data.rewards   || [];
    var branding  = data.branding  || {};
    var accent    = branding.accent || '#c9a96e';
    var brandName = branding.name  || 'Rewards';
    var dashUrl   = opts.dashboardUrl;
    var tierName  = member.tier ? member.tier.name : 'Member';
    var tierColor = member.tier ? member.tier.color : accent;
    var prog      = tierProgress(member);
    var activeCoupons = coupons.filter(function (c) { return !c.isUsed; });

    // Override CSS accent with live branding colour
    container.style.setProperty('--sgm-accent', accent);
    if (member.tier) {
      container.style.setProperty('--sgm-accent', tierColor);
    }

    container.innerHTML = '';
    var card = el('div', { className: 'sgm-dash' });

    // ── Header ─────────────────────────────────────────────
    card.appendChild(
      el('div', { className: 'sgm-dash__header' }, [
        el('div', { className: 'sgm-dash__brand' }, brandName + ' Rewards'),
        el('div', {
          className: 'sgm-dash__tier-badge',
          style: 'background:' + tierColor + '22;color:' + tierColor + ';border-color:' + tierColor + '55;',
        }, tierName),
      ])
    );

    // ── Points block ───────────────────────────────────────
    var pointsBlock = el('div', { className: 'sgm-dash__points-block' });
    pointsBlock.appendChild(el('div', { className: 'sgm-dash__points-label' }, 'Your points balance'));
    pointsBlock.appendChild(
      el('div', {
        className: 'sgm-dash__points-value',
        style: 'color:' + accent + ';',
      }, fmtPoints(member.pointsBalance))
    );
    pointsBlock.appendChild(
      el('div', { className: 'sgm-dash__points-sub' }, 'pts available')
    );

    // Progress bar (only if not max tier)
    if (prog.pct < 100) {
      var progressWrap = el('div', { className: 'sgm-dash__progress-wrap' });
      var bar = el('div', { className: 'sgm-dash__progress-bar' });
      var fill = el('div', {
        className: 'sgm-dash__progress-fill',
        style: 'width:0%;background:' + accent + ';',
      });
      bar.appendChild(fill);
      progressWrap.appendChild(bar);
      progressWrap.appendChild(el('div', { className: 'sgm-dash__progress-label' }, prog.label));
      pointsBlock.appendChild(progressWrap);
      // Animate fill after paint
      setTimeout(function () { fill.style.width = prog.pct + '%'; }, 50);
    }

    card.appendChild(pointsBlock);

    // ── Stats ──────────────────────────────────────────────
    card.appendChild(
      el('div', { className: 'sgm-dash__stats' }, [
        el('div', { className: 'sgm-dash__stat' }, [
          el('div', { className: 'sgm-dash__stat-value' }, String(member.orderCount || 0)),
          el('div', { className: 'sgm-dash__stat-label' }, 'Orders'),
        ]),
        el('div', { className: 'sgm-dash__stat' }, [
          el('div', { className: 'sgm-dash__stat-value' }, fmtCurrency(member.lifetimeSpend || 0)),
          el('div', { className: 'sgm-dash__stat-label' }, 'Lifetime spend'),
        ]),
      ])
    );

    // ── Active vouchers ────────────────────────────────────
    if (activeCoupons.length > 0) {
      var voucherSection = el('div', { className: 'sgm-dash__vouchers' });
      voucherSection.appendChild(
        el('div', { className: 'sgm-dash__section-title' },
          activeCoupons.length + ' active voucher' + (activeCoupons.length > 1 ? 's' : '')
        )
      );
      activeCoupons.slice(0, 3).forEach(function (c) {
        voucherSection.appendChild(renderVoucher(c));
      });
      card.appendChild(voucherSection);
    }

    // ── Actions ────────────────────────────────────────────
    var actions = el('div', { className: 'sgm-dash__actions' });

    var dashBtn = el('a', {
      className: 'sgm-dash__btn sgm-dash__btn--primary',
      href: dashUrl,
      style: 'background:' + accent + ';color:#000;',
    }, 'View My Dashboard →');
    actions.appendChild(dashBtn);

    // Show rewards count if any
    if (rewards.length > 0) {
      var affordable = rewards.filter(function (r) {
        return r.pointsCost <= (member.pointsBalance || 0);
      });
      if (affordable.length > 0) {
        actions.appendChild(
          el('a', {
            className: 'sgm-dash__btn sgm-dash__btn--secondary',
            href: dashUrl + '#rewards',
          }, affordable.length + ' reward' + (affordable.length > 1 ? 's' : '') + ' to redeem')
        );
      }
    }

    card.appendChild(actions);
    container.appendChild(card);
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init(container) {
    var customerId  = container.dataset.customerId;
    var shop        = container.dataset.shop || 'scentjourner.myshopify.com';
    var apiBase     = container.dataset.apiBase || 'https://rewards.scentjourner.com';
    var dashboardUrl = container.dataset.dashboardUrl || 'https://rewards.scentjourner.com/storefront';

    // Append customer_id to dashboard URL so the storefront page can auth
    if (customerId) {
      dashboardUrl += (dashboardUrl.indexOf('?') >= 0 ? '&' : '?') +
        'customer_id=' + encodeURIComponent(customerId) + '&shop=' + encodeURIComponent(shop);
    }

    if (!customerId) {
      container.innerHTML = '';
      container.appendChild(
        el('div', { className: 'sgm-dash__login' }, [
          el('p', { style: 'margin-bottom:16px;' }, 'Log in to see your rewards.'),
          el('a', {
            className: 'sgm-dash__btn sgm-dash__btn--primary',
            href: '/account/login?return_url=' + encodeURIComponent(window.location.pathname),
          }, 'Log In'),
        ])
      );
      return;
    }

    // Show skeleton immediately
    renderSkeleton(container);

    // Fetch data
    var url = apiBase + '/api/members/storefront-data?' +
      'customer_id=' + encodeURIComponent(customerId) +
      '&shop='        + encodeURIComponent(shop);

    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.error) {
          renderError(container, data.error);
        } else {
          renderDashboard(container, data, { dashboardUrl: dashboardUrl });
        }
      })
      .catch(function () {
        renderError(container, 'Could not load your rewards. Please try again.');
      });
  }

  // ─── Boot on DOMContentLoaded ─────────────────────────────────────────────

  function boot() {
    document.querySelectorAll('[data-sgm-dashboard]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();